/**
 * UrlGeneratorPage — SNS 広告 / QR コード等で発行するタグ付き URL の生成画面。
 *
 * 機能:
 *   - LP / フォームをセレクト
 *   - キャンペーン（任意）をセレクト
 *   - referral タグを入力（半角英数 + _ - . で 32 文字以内）
 *   - 生成 URL を表示してコピー or QR 画像（base64 SVG）にダウンロード
 *
 * URL フォーマット:
 *   /l/{slug}?campaignId=...&src=referral&ref={tag}
 *   /public/forms/{formId}?campaignId=...&src=referral&ref={tag}
 *
 * 受信側:
 *   - recordLandingView / submitForm 共に src='referral' + metadata.refTag={tag} で記録
 *   - useCampaignFunnel は referral を独立 source として集計
 *   - ContactRow タイムラインに「タグ付き URL」バッジで表示
 */
import { useMemo, useState } from 'react';
import { Copy, Download, ExternalLink, Link as LinkIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { RefTagInput } from '@/components/common/RefTagInput';
import { Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useForms } from '@/hooks/useForms';
import { useLandings } from '@/hooks/useLandings';

const TAG_RE = /^[A-Za-z0-9_.-]{1,32}$/;
const NONE = '__none__';

type TargetKind = 'landing' | 'form';

/**
 * URL 生成ビュー（リフェラル統合ページのタブ用）。Screen ラッパなし。
 */
export function UrlGeneratorView() {
  const landingsQ = useLandings();
  const formsQ = useForms();
  const campaignsQ = useCampaigns();

  const [kind, setKind] = useState<TargetKind>('landing');
  const [targetId, setTargetId] = useState<string>('');
  const [campaignId, setCampaignId] = useState<string>('');
  const [tag, setTag] = useState<string>('');

  const selectedLanding = useMemo(
    () => (landingsQ.data ?? []).find((l) => l.id === targetId),
    [landingsQ.data, targetId],
  );

  const tagValid = !tag || TAG_RE.test(tag);

  const generatedUrl = useMemo(() => {
    if (!targetId) return null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    let path = '';
    if (kind === 'landing') {
      const slug = selectedLanding?.slug;
      if (!slug) return null;
      path = `/l/${slug}`;
    } else {
      path = `/public/forms/${targetId}`;
    }
    const params: Array<[string, string]> = [];
    if (campaignId) params.push(['campaignId', campaignId]);
    // referral 経由は src=referral を必ず付与（タグ未入力でもチャネル識別可能）
    params.push(['src', 'referral']);
    if (tag && tagValid) params.push(['ref', tag]);
    const qs = params
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return `${origin}${path}?${qs}`;
  }, [kind, targetId, selectedLanding, campaignId, tag, tagValid]);

  const qrPngHref = useMemo(() => {
    if (!generatedUrl) return null;
    // Google Chart API は廃止のため、外部依存なしで簡易 QR を出すには SVG ライブラリが要る。
    // ここでは公開 QR API を使用（コピー or 右クリック保存用、必要なら差し替え）。
    return `https://api.qrserver.com/v1/create-qr-code/?size=480x480&data=${encodeURIComponent(generatedUrl)}`;
  }, [generatedUrl]);

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      toast.success('URL をコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  const isLoading = landingsQ.isLoading || formsQ.isLoading || campaignsQ.isLoading;

  return (
    <div>
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 入力 */}
          <div className="space-y-4 rounded border border-[var(--border)] bg-[var(--bg-surface)] p-4">
            <div>
              <Label className="text-xs">遷移先の種別</Label>
              <Select value={kind} onValueChange={(v) => { setKind(v as TargetKind); setTargetId(''); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landing">ランディング (/l/{':slug'})</SelectItem>
                  <SelectItem value="form">フォーム (/public/forms/{':id'})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">
                {kind === 'landing' ? 'ランディングを選択' : 'フォームを選択'}
              </Label>
              <Select
                value={targetId || NONE}
                onValueChange={(v) => setTargetId(v === NONE ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— 選択 —</SelectItem>
                  {kind === 'landing'
                    ? (landingsQ.data ?? [])
                        .filter((l) => l.status === 'published' && l.slug)
                        .map((l) => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.title} <span className="text-[10px] text-[var(--text-muted)]">/{l.slug}</span>
                          </SelectItem>
                        ))
                    : (formsQ.data ?? []).map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">紐付けキャンペーン（任意）</Label>
              <Select
                value={campaignId || NONE}
                onValueChange={(v) => setCampaignId(v === NONE ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="未指定" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>未指定</SelectItem>
                  {(campaignsQ.data ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || '(無題)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <RefTagInput
              id="urlgen-tag"
              label="REFERRAL タグ（自由記述またはプリセット選択）"
              value={tag}
              onChange={setTag}
            />
          </div>

          {/* プレビュー */}
          <div className="space-y-4 rounded border border-[var(--border)] bg-[var(--bg-surface)] p-4">
            <div>
              <Label className="text-xs flex items-center gap-1">
                <LinkIcon className="size-3" /> 生成 URL
              </Label>
              {generatedUrl ? (
                <div className="space-y-2">
                  <div className="font-mono text-[11px] break-all bg-[var(--bg-elevated)] border border-[var(--border)] rounded p-2">
                    {generatedUrl}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={handleCopy}
                    >
                      <Copy className="size-3.5 mr-1" />
                      URL をコピー
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(generatedUrl, '_blank')}
                    >
                      <ExternalLink className="size-3.5 mr-1" />
                      開く
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-[var(--text-muted)] mt-1">
                  遷移先を選択すると URL が生成されます。
                </p>
              )}
            </div>

            {qrPngHref && (
              <div>
                <Label className="text-xs">QR コード（外部生成サービス利用）</Label>
                <div className="mt-1 flex items-start gap-3">
                  <img
                    src={qrPngHref}
                    alt="QR"
                    className="size-40 border border-[var(--border)] rounded bg-white"
                  />
                  <div className="text-[11px] text-[var(--text-muted)] space-y-2">
                    <p>
                      右クリック → 「画像を保存」で PNG ダウンロードできます。
                      高解像度・社内サーバー生成が必要なら本機能差し替え予定。
                    </p>
                    <a
                      href={qrPngHref}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-1 text-[var(--accent)]"
                    >
                      <Download className="size-3" />
                      画像を別タブで開く
                    </a>
                  </div>
                </div>
              </div>
            )}

            <div className="text-[11px] text-[var(--text-muted)] space-y-1 pt-2 border-t border-[var(--border)]">
              <p className="font-medium">活用例</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>SNS 広告ごとに異なる ref タグ（fb_ad_a, fb_ad_b 等）で広告 A/B テスト</li>
                <li>紙チラシに QR を印刷 → 印刷ロットごとに ref タグ（chirashi_2026q3）</li>
                <li>YouTube 概要欄リンク（yt_episode42）等、外部チャネルの効果計測</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
